import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { AttributeTypeFormModal, AttributeValuesSheet } from './components';
import { AttributesPageSkeleton } from './AttributesPageSkeleton';
import { AttributesPageView } from './AttributesPageView';
import { useAttributesPage } from './useAttributesPage';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * Attributes page — the global attribute dictionary. Owns its data hook and an
 * `ErrorBoundary`; the throw lives in the inner Content child (React boundaries
 * don't catch a component's own throw). The type form modal and the values
 * drawer render outside the boundary so a table render failure can't take them
 * down.
 */
export const AttributesPage: React.FC = () => {
    const page = useAttributesPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('attributes.components.dictionary.errorTitle'),
            errorMessage: t('attributes.components.dictionary.errorMessage'),
            errorRetry: t('attributes.components.dictionary.errorRetry'),
        }),
        [t],
    );

    return (
        <div
            className="mx-auto flex w-full max-w-7xl flex-col gap-8"
            {...getNodeTestIdProp('attributes-page')}
            id="attributes-page"
        >
            <ErrorBoundary
                data-testid="attribute-dictionary-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="attribute-dictionary-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <AttributesPageContent page={page} />
            </ErrorBoundary>
            <AttributeTypeFormModal
                error={page.typeFormError}
                initialValues={page.typeFormInitialValues}
                isOpen={page.isTypeFormOpen}
                isSubmitting={page.isTypeSubmitting}
                mode={page.typeFormMode}
                onClose={page.onCloseTypeForm}
                onSubmit={page.onSubmitType}
            />
            <AttributeValuesSheet
                isOpen={page.isValuesSheetOpen}
                onClose={page.onCloseValuesSheet}
                type={page.selectedType}
            />
        </div>
    );
};

type AttributesPageContentProps = {
    page: ReturnType<typeof useAttributesPage>;
};

const AttributesPageContent: React.FC<AttributesPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    if (page.isLoading || page.types === null) {
        return <AttributesPageSkeleton />;
    }

    return (
        <AttributesPageView
            onAddType={page.onAddType}
            onDeleteType={page.onDeleteType}
            onEditType={page.onEditType}
            onManageValues={page.onManageValues}
            onPromoteValue={page.onPromoteValue}
            onRetryPending={page.onRetryPending}
            onSearchChange={page.onSearchChange}
            onTabChange={page.onTabChange}
            pending={page.pending}
            pendingError={page.pendingError}
            search={page.search}
            tab={page.tab}
            types={page.types}
        />
    );
};
